import React from 'react';
import { Link } from 'react-router-dom';
import StructuredData from './StructuredData';
import '../styles/PricingPage.css';

const PricingPage: React.FC = () => {
  return (
    <div className="pricing-page">
      {/* SEO Structured Data */}
      <StructuredData
        type="faq"
        data={{
          faqs: [
            {
              question: "How much does Larry's Help cost?",
              answer: "Larry's Help is free to use: all calculators with weekly scenario limits, bookkeeping for one business, and scheduling booking links. Our Supporters Club costs just $10 CAD per year (less than $1/month) and unlocks everything: unlimited scenarios, bookkeeping for up to 10 businesses, scheduling Pro features, and priority support. No auto-renewal - you stay in control."
            },
            {
              question: "What's included in the free version?",
              answer: "The free version includes access to all our Canadian financial calculators: Car Lease vs Buy, Rent vs Buy House, RRSP Calculator, Career Comparison, Retirement Planning, and Personal Financial Forecast. You can create and save up to 10 scenarios per week."
            },
            {
              question: "What do I get with the Supporters Club?",
              answer: "One membership covers everything: unlimited saved scenarios (no weekly limits), advanced calculator features, bookkeeping for up to 10 businesses with 10,000 journal entries, scheduling Pro features (hide branding, API keys, staff logins, payment collection), and priority email support. All for just $10 CAD per year."
            },
            {
              question: "Does Larry's Help auto-renew subscriptions?",
              answer: "No! We don't believe in auto-renewal or subscription traps. When you join the Supporters Club, you pay once for one year. We'll send you a reminder email before your membership expires, but you choose whether to renew. Your money, your choice."
            },
            {
              question: "Can I cancel my Supporters Club membership?",
              answer: "Since there's no auto-renewal, there's nothing to cancel! Your membership simply expires after one year. You can choose to renew or continue using the free version - no pressure, no hassle."
            },
            {
              question: "How do I pay for the Supporters Club?",
              answer: "We accept all major credit cards through Stripe, a secure payment processor trusted by millions. Your payment information is encrypted and we never store your card details. You'll receive an instant receipt via email."
            },
            {
              question: "Is my payment information secure?",
              answer: "Yes! All payments are processed through Stripe, one of the world's most trusted payment platforms. Your card information is encrypted with bank-level security and never stored on our servers."
            },
            {
              question: "Can I upgrade from free to Supporters Club anytime?",
              answer: "Absolutely! You can upgrade to the Supporters Club at any time from your account page. Your membership starts immediately and lasts for one full year from the date of purchase."
            }
          ]
        }}
      />

      <div className="pricing-hero">
        <h1>Simple, Transparent Pricing</h1>
        <p className="hero-subtitle">
          No hidden fees. No auto-renewal. No pressure.
        </p>
      </div>

      <div className="pricing-container">
        {/* Free Tier */}
        <div className="pricing-tier free-tier">
          <div className="tier-badge">Most Popular</div>
          <h2>Free</h2>
          <div className="price">
            <span className="amount">$0</span>
            <span className="period">forever</span>
          </div>
          <p className="tier-description">
            Perfect for exploring our calculators and making occasional financial decisions
          </p>

          <div className="features">
            <h3>What's Included:</h3>
            <ul>
              <li>✅ All Canadian financial calculators</li>
              <li>✅ Car Lease vs Buy Calculator</li>
              <li>✅ Rent vs Buy House Calculator</li>
              <li>✅ RRSP vs TFSA Calculator</li>
              <li>✅ Career Comparison Calculator</li>
              <li>✅ Retirement Planning Calculator</li>
              <li>✅ Personal Financial Forecast</li>
              <li>✅ Save up to 10 scenarios per week</li>
              <li>✅ Bookkeeping: 1 business, 500 journal entries</li>
              <li>✅ Scheduling: booking links and calendar sync</li>
              <li>✅ Basic email support</li>
            </ul>
          </div>

          <Link to="/account" className="cta-button secondary">
            Get Started Free
          </Link>
        </div>

        {/* Supporters Club Tier */}
        <div className="pricing-tier supporters-tier featured">
          <div className="tier-badge">Best Value</div>
          <h2>Supporters Club</h2>
          <div className="price">
            <span className="amount">$10</span>
            <span className="period">CAD/year</span>
          </div>
          <p className="price-note">Less than $1/month</p>
          <p className="tier-description">
            One membership unlocks everything: calculators, bookkeeping, and scheduling
          </p>

          <div className="features">
            <h3>Everything in Free, plus:</h3>
            <ul>
              <li>⭐ Unlimited saved scenarios</li>
              <li>⭐ No weekly limits</li>
              <li>⭐ Advanced calculator features</li>
              <li>⭐ Bookkeeping for up to 10 businesses, 10,000 journal entries</li>
              <li>⭐ Scheduling Pro: hide branding, API keys, staff logins, payment collection</li>
              <li>⭐ Priority email support</li>
              <li>⭐ Help us build better tools</li>
              <li>⭐ Support Canadian financial literacy</li>
            </ul>
          </div>

          <div className="trust-badges">
            <div className="trust-badge">
              <strong>✅ No Auto-Renewal</strong>
              <p>You stay in control. We'll remind you before expiration.</p>
            </div>
            <div className="trust-badge">
              <strong>✅ Secure Payment</strong>
              <p>Encrypted by Stripe. Bank-level security.</p>
            </div>
            <div className="trust-badge">
              <strong>✅ Instant Access</strong>
              <p>Upgrade takes 30 seconds. Start using immediately.</p>
            </div>
          </div>

          <Link to="/account" className="cta-button primary">
            Join Supporters Club - $10/year
          </Link>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="pricing-faq">
        <h2>Frequently Asked Questions</h2>

        <div className="faq-grid">
          <div className="faq-item">
            <h3>Why no auto-renewal?</h3>
            <p>
              We believe in transparency and user control. Too many services make it hard to cancel
              or charge you unexpectedly. With Larry's Help, you pay once for one year. We'll remind
              you before it expires, but you decide whether to renew. No tricks, no traps.
            </p>
          </div>

          <div className="faq-item">
            <h3>How is this different from other financial tools?</h3>
            <p>
              Most financial planning tools cost $50-$200+ per year with complex pricing and auto-renewal.
              We're built specifically for Canadians, with accurate tax rates, RRSP/TFSA calculations,
              and real-world scenarios. All for just $10/year with no commitment.
            </p>
          </div>

          <div className="faq-item">
            <h3>Can I try before I buy?</h3>
            <p>
              Absolutely! Our free tier includes full access to all calculators. Use them as much as
              you want with the 10 scenarios/week limit. When you're ready for unlimited access,
              upgrade to Supporters Club for $10/year.
            </p>
          </div>

          <div className="faq-item">
            <h3>What if I'm not satisfied?</h3>
            <p>
              We stand behind our tools! The Supporters Club is just $10/year — less than a coffee a month.
              This one-time annual payment helps us maintain free access for everyone. Due to the low cost
              and immediate access to all features, refunds are not available.
            </p>
          </div>

          <div className="faq-item">
            <h3>Do you offer team or family plans?</h3>
            <p>
              Currently, memberships are individual. However, at $10/year, it's affordable for each
              family member to have their own account with private scenarios. Interested in a family
              plan? Let us know!
            </p>
          </div>

          <div className="faq-item">
            <h3>Will prices ever increase?</h3>
            <p>
              We're committed to keeping Larry's Help affordable for all Canadians. If we ever need
              to adjust pricing, existing Supporters Club members will be grandfathered in at their
              original rate. That's our promise.
            </p>
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="pricing-comparison">
        <h2>Free vs Supporters Club</h2>
        <table className="comparison-table">
          <thead>
            <tr>
              <th>Feature</th>
              <th>Free</th>
              <th>Supporters Club</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>All Financial Calculators</td>
              <td>✅</td>
              <td>✅</td>
            </tr>
            <tr>
              <td>Saved Scenarios</td>
              <td>10/week</td>
              <td>Unlimited</td>
            </tr>
            <tr>
              <td>Advanced Features</td>
              <td>❌</td>
              <td>✅</td>
            </tr>
            <tr>
              <td>Priority Support</td>
              <td>❌</td>
              <td>✅</td>
            </tr>
            <tr>
              <td>Auto-Renewal</td>
              <td>N/A</td>
              <td>Never</td>
            </tr>
            <tr>
              <td>Price</td>
              <td>$0</td>
              <td>$10 CAD/year</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Bottom CTA */}
      <div className="pricing-bottom-cta">
        <h2>Ready to make better financial decisions?</h2>
        <p>Join thousands of Canadians using Larry's Help to plan their financial future.</p>
        <div className="cta-buttons">
          <Link to="/account" className="cta-button primary large">
            Join Supporters Club - $10/year
          </Link>
          <Link to="/" className="cta-button secondary large">
            Start Free
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
